import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

const VEHICLE_LABELS = { twoWheeler: 'Two-wheeler', fourWheeler: 'Four-wheeler' };
const VEHICLE_OPTIONS = Object.entries(VEHICLE_LABELS).map(([value, label]) => ({ value, label }));

const initials = (name = '') => String(name).trim().split(/\s+/).slice(0, 2)
  .map(part => part.charAt(0)).join('').toUpperCase() || '?';

const ChevronIcon = () => <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6 8 4 4 4-4" /></svg>;
const CheckIcon = () => <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m4.5 10.5 3.2 3.2 7.8-8" /></svg>;
const LockIcon = () => <svg viewBox="0 0 20 20" aria-hidden="true"><rect x="4.5" y="8.5" width="11" height="8" rx="2" /><path d="M7 8.5V6a3 3 0 0 1 6 0v2.5" /></svg>;
const UserPlusIcon = () => <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="7.5" cy="6" r="3" /><path d="M2.5 16c.4-3.2 2-4.8 5-4.8 1.4 0 2.5.3 3.3 1M14.5 8v6M11.5 11h6" /></svg>;

const VehicleIcon = ({ type }) => {
  if (type === 'twoWheeler') return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5.5" cy="16.5" r="3" /><circle cx="18.5" cy="16.5" r="3" /><path d="m8 16.5 3.2-6h3.3l4 6M9.5 16.5h6.2M10.5 8h3" /></svg>;
  if (type === 'fourWheeler') return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 14 1.8-5h12.4l1.8 5v4H4v-4Z" /><path d="M7 18v2m10-2v2M6 14h12" /><circle cx="7" cy="15.5" r=".8" /><circle cx="17" cy="15.5" r=".8" /></svg>;
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7h12v10H3zM15 11h3l3 3v3h-6z" /><circle cx="7" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></svg>;
};

const trapFocus = (event, container) => {
  if (event.key !== 'Tab' || !container) return;
  const focusable = [...container.querySelectorAll('input:not([disabled]), button:not([disabled]), [tabindex="0"]')];
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const useAnchoredMenu = (open, anchorRef, menuRef, preferredWidth, estimatedHeight) => {
  const [style, setStyle] = useState({});
  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const width = Math.max(preferredWidth, rect.width);
    const height = menuRef.current?.offsetHeight || estimatedHeight;
    const below = window.innerHeight - rect.bottom;
    const openUp = below < height + 12 && rect.top > below;
    setStyle({
      position: 'fixed',
      width,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      top: openUp ? Math.max(8, rect.top - height - 6) : Math.min(window.innerHeight - height - 8, rect.bottom + 6),
      zIndex: 10050,
    });
  }, [anchorRef, estimatedHeight, menuRef, preferredWidth]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const frame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);
  return style;
};

const SavedStateIcon = ({ saving, saved, fallback }) => {
  if (saving) return <span className="shipment-cell-spinner" aria-label="Saving" />;
  if (saved) return <span className="shipment-cell-saved" aria-label="Saved"><CheckIcon /></span>;
  return fallback;
};

export const ShipmentOrdersCell = ({ shipment, mobile = false, dragDrop = null, canOpenOrders = true }) => {
  const orderIds = shipment.orderIds || [];
  const orderCount = Number(shipment.orderCount || orderIds.length || 0);
  const dragDepthRef = useRef(0);
  const dragEnabled = Boolean(dragDrop?.enabled && !mobile);

  useEffect(() => {
    if (!dragDrop?.active) dragDepthRef.current = 0;
  }, [dragDrop?.active]);

  const handleDragEnter = event => {
    if (!dragDrop?.active) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    dragDrop.onTargetChange(shipment.id);
  };

  const handleDragOver = event => {
    if (!dragDrop?.active) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = dragDrop.canDrop ? 'move' : 'none';
  };

  const handleDragLeave = () => {
    if (!dragDrop?.active) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0 && dragDrop.isTarget) dragDrop.onTargetChange(null);
  };

  const handleDrop = event => {
    if (!dragDrop?.active) return;
    dragDepthRef.current = 0;
    dragDrop.onDrop(event, shipment);
  };

  return <div
    className={`shipment-order-ids ${mobile ? 'is-mobile' : ''} ${dragEnabled ? 'is-drag-enabled' : ''} ${dragDrop?.isSource ? 'is-drag-source' : ''} ${dragDrop?.isTarget ? `is-drop-target ${dragDrop.canDrop ? 'is-drop-valid' : 'is-drop-invalid'}` : ''} ${dragDrop?.moving ? 'is-moving-order' : ''}`}
    onDragEnter={handleDragEnter}
    onDragOver={handleDragOver}
    onDragLeave={handleDragLeave}
    onDrop={handleDrop}
  >
    <span>{orderCount} {orderCount === 1 ? 'order' : 'orders'}{dragEnabled && <em>Drag to move</em>}</span>
    {orderIds.length ? <div role="group" aria-label={`${orderIds.length} order IDs`} className={`shipment-order-list ${orderIds.length > 10 ? 'is-two-column' : ''}`}>
      {orderIds.map(orderId => canOpenOrders ? <Link
          key={orderId}
          to={`/orders/${orderId}`}
          target="_blank"
          rel="noopener noreferrer"
          title={dragEnabled ? `Drag ${orderId} to another shipment, or click to open the order` : orderId}
          draggable={dragEnabled}
          className={`${dragEnabled ? 'is-draggable' : ''} ${dragDrop?.activeOrderId === orderId ? 'is-dragging' : ''}`}
          onDragStart={dragEnabled ? event => dragDrop.onDragStart(event, shipment, orderId) : undefined}
          onDragEnd={dragEnabled ? dragDrop.onDragEnd : undefined}
        >{orderId}</Link> : <span key={orderId} className="shipment-order-id-text">{orderId}</span>)}
    </div> : <small>Order IDs unavailable</small>}
    {dragDrop?.isTarget && <div className="shipment-order-drop-state" role="status"><strong>{dragDrop.canDrop ? `Move to ${shipment.id}` : dragDrop.dropReason}</strong><span>{dragDrop.canDrop ? 'Release to move the complete order' : 'This shipment cannot accept the order'}</span></div>}
    {dragDrop?.moving && <div className="shipment-order-drop-state is-saving" role="status"><strong>Moving order…</strong><span>Rebuilding both delivery routes</span></div>}
  </div>;
};

export const ShipmentDriverCell = ({ shipment, drivers, saving, onChange, mobile = false, editable: canEdit = true }) => {
  const editable = canEdit && shipment.status === 'pending';
  const assigned = shipment.assignedTo;
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const savedTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [saved, setSaved] = useState(false);
  const menuStyle = useAnchoredMenu(open, anchorRef, menuRef, 310, 330);

  const options = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matched = drivers.filter(driver => !query || `${driver.name || ''} ${driver.email || ''}`.toLowerCase().includes(query));
    return assigned ? [...matched, { id: '', name: 'Unassign driver', email: 'Return shipment to the unassigned queue', unassign: true }] : matched;
  }, [assigned, drivers, search]);

  useEffect(() => {
    if (!open) return undefined;
    setActiveIndex(0);
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus());
    const close = event => {
      if (!menuRef.current?.contains(event.target) && !anchorRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => { window.cancelAnimationFrame(frame); document.removeEventListener('mousedown', close); };
  }, [open]);

  useEffect(() => () => window.clearTimeout(savedTimerRef.current), []);

  const selectDriver = async driverId => {
    setOpen(false);
    const succeeded = await onChange(shipment, driverId);
    if (!succeeded) return;
    setSaved(true);
    window.clearTimeout(savedTimerRef.current);
    savedTimerRef.current = window.setTimeout(() => setSaved(false), 1400);
  };

  const handleMenuKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      anchorRef.current?.focus();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(index => options.length ? (index + direction + options.length) % options.length : 0);
      return;
    }
    if (event.key === 'Enter' && options[activeIndex]) {
      event.preventDefault();
      selectDriver(options[activeIndex].id);
      return;
    }
    trapFocus(event, menuRef.current);
  };

  const displayName = assigned?.name || assigned?.email || 'Unassigned';
  const lockedTitle = !canEdit ? 'Assigned delivery partner' : "Driver can't be changed once the ride starts";
  if (!editable) return <div className={`shipment-driver-cell is-locked ${mobile ? 'is-mobile' : ''}`} tabIndex="0" title={lockedTitle}>
    <span className={`shipment-driver-avatar ${assigned ? '' : 'is-empty'}`}>{initials(displayName)}</span>
    <span className="shipment-driver-copy"><strong title={displayName}>{displayName}</strong><small title={assigned?.email || 'No driver assigned'}>{assigned?.email || 'No driver assigned'}</small></span>
    <span className="shipment-cell-lock"><LockIcon /></span>
  </div>;

  return <>
    <button ref={anchorRef} type="button" className={`shipment-driver-cell is-editable ${assigned ? 'is-assigned' : 'is-unassigned'} ${mobile ? 'is-mobile' : ''}`} disabled={saving} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(value => !value)} onKeyDown={event => { if (!open && ['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); setOpen(true); } }}>
      {assigned ? <><span className="shipment-driver-avatar">{initials(displayName)}</span><span className="shipment-driver-copy"><strong title={displayName}>{displayName}</strong><small title={assigned.email}>{assigned.email}</small></span></> : <><span className="shipment-cell-leading-icon"><UserPlusIcon /></span><span className="shipment-driver-copy"><strong>Assign driver</strong></span></>}
      <SavedStateIcon saving={saving} saved={saved} fallback={<span className="shipment-cell-chevron"><ChevronIcon /></span>} />
    </button>
    {open && createPortal(<div ref={menuRef} className="shipment-cell-popover shipment-driver-popover" style={menuStyle} onKeyDown={handleMenuKeyDown}>
      <div className="shipment-driver-search"><svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5" /><path d="m12.3 12.3 4 4" /></svg><input ref={searchRef} role="combobox" aria-expanded="true" aria-controls={`driver-options-${shipment.id}`} aria-activedescendant={options[activeIndex] ? `driver-${shipment.id}-${activeIndex}` : undefined} value={search} onChange={event => { setSearch(event.target.value); setActiveIndex(0); }} placeholder="Search driver by name or email" /></div>
      <div id={`driver-options-${shipment.id}`} className="shipment-driver-options" role="listbox" aria-label={`Drivers for ${shipment.id}`}>
        {!options.length && <div className="shipment-cell-menu-empty">No matching delivery partner</div>}
        {options.map((driver, index) => <button id={`driver-${shipment.id}-${index}`} type="button" role="option" aria-selected={String(assigned?.id || '') === String(driver.id)} className={`${index === activeIndex ? 'is-active' : ''} ${driver.unassign ? 'is-unassign' : ''}`} key={driver.unassign ? 'unassign' : driver.id} onMouseEnter={() => setActiveIndex(index)} onClick={() => selectDriver(driver.id)}>
          <span className={`shipment-driver-avatar ${driver.unassign ? 'is-empty' : ''}`}>{driver.unassign ? '−' : initials(driver.name || driver.email)}</span><span><strong>{driver.name || driver.email}</strong><small>{driver.email}</small></span>{String(assigned?.id || '') === String(driver.id) && <CheckIcon />}
        </button>)}
      </div>
    </div>, document.body)}
  </>;
};

export const ShipmentVehicleTypeCell = ({ shipment, saving, onChange, editable, mobile = false }) => {
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const savedTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [saved, setSaved] = useState(false);
  const options = VEHICLE_OPTIONS;
  const menuStyle = useAnchoredMenu(open, anchorRef, menuRef, 190, 120);
  const label = VEHICLE_LABELS[shipment.vehicleType] || shipment.vehicleType || 'Vehicle';
  const lockedReason = shipment.status === 'pending'
    ? 'Only a Delivery Admin can change vehicle type'
    : "Vehicle type can't be changed once the ride starts";

  useEffect(() => {
    if (!open) return undefined;
    setActiveIndex(Math.max(0, options.findIndex(option => option.value === shipment.vehicleType)));
    const close = event => {
      if (!menuRef.current?.contains(event.target) && !anchorRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, options, shipment.vehicleType]);

  useEffect(() => () => window.clearTimeout(savedTimerRef.current), []);

  const selectType = async value => {
    setOpen(false);
    const succeeded = await onChange(shipment, value);
    if (!succeeded) return;
    setSaved(true);
    window.clearTimeout(savedTimerRef.current);
    savedTimerRef.current = window.setTimeout(() => setSaved(false), 1400);
  };

  const handleKeyDown = event => {
    if (!open && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault(); setOpen(true); return;
    }
    if (event.key === 'Escape') { event.preventDefault(); setOpen(false); anchorRef.current?.focus(); return; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(index => (index + direction + options.length) % options.length);
      return;
    }
    if (event.key === 'Enter' && options[activeIndex]) { event.preventDefault(); selectType(options[activeIndex].value); return; }
    trapFocus(event, menuRef.current);
  };

  if (!editable) return <div className={`shipment-vehicle-cell is-locked ${mobile ? 'is-mobile' : ''}`} tabIndex="0" title={lockedReason}><span className="shipment-vehicle-cell-icon"><VehicleIcon type={shipment.vehicleType} /></span><strong>{label}</strong><span className="shipment-cell-lock"><LockIcon /></span></div>;

  return <>
    <button ref={anchorRef} type="button" className={`shipment-vehicle-cell is-editable ${mobile ? 'is-mobile' : ''}`} disabled={saving} title={shipment.vehicleTypeAdvisory || 'Change vehicle type'} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(value => !value)} onKeyDown={handleKeyDown}><span className="shipment-vehicle-cell-icon"><VehicleIcon type={shipment.vehicleType} /></span><strong>{label}</strong><SavedStateIcon saving={saving} saved={saved} fallback={<span className="shipment-cell-chevron"><ChevronIcon /></span>} /></button>
    {open && createPortal(<div ref={menuRef} className="shipment-cell-popover shipment-vehicle-popover" style={menuStyle} role="listbox" aria-label={`Vehicle type for ${shipment.id}`} onKeyDown={handleKeyDown}>{options.map((option, index) => <button type="button" role="option" aria-selected={shipment.vehicleType === option.value} className={index === activeIndex ? 'is-active' : ''} key={option.value} onMouseEnter={() => setActiveIndex(index)} onClick={() => selectType(option.value)}><span className="shipment-vehicle-cell-icon"><VehicleIcon type={option.value} /></span><strong>{option.label}</strong>{shipment.vehicleType === option.value && <CheckIcon />}</button>)}</div>, document.body)}
  </>;
};
